import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '@hooks/useTranslation';

/**
 * Why the address is already taken. The two cases need different guidance:
 * an invited user is waiting on an email they may not have opened, while an existing
 * account holder simply needs to sign in and create the organization from inside the app.
 */
export type AccountExistsReason = 'invited' | 'registered';

interface AccountExistsProps {
  setShowAlreadyInvited: (show: boolean) => void;
  invitedEmail: string;
  reason?: AccountExistsReason;
}

const AccountExists: React.FC<AccountExistsProps> = ({
  setShowAlreadyInvited,
  invitedEmail,
  reason = 'invited',
}) => {
  const tLogin = useTranslation('login');
  const history = useHistory();
  const isRegistered = reason === 'registered';

  return (
    <div className="flex justify-center items-start pt-24 pb-8 px-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-blue-600 dark:text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isRegistered ? tLogin.alreadyHaveAccount : tLogin.accountAlreadyExists}
          </h2>
          {invitedEmail && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {invitedEmail}
            </p>
          )}
        </div>

        <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
          {isRegistered ? (
            <>
              <p>{tLogin.accountRegisteredExplain}</p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                  {tLogin.toCreateAnotherOrg}
                </p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700 dark:text-blue-300">
                  <li>{tLogin.accountExistsStep1}</li>
                  <li>{tLogin.accountExistsStep2}</li>
                  <li>{tLogin.accountExistsStep3}</li>
                </ol>
              </div>
            </>
          ) : (
            <>
              <p>{tLogin.accountInvitedExplain}</p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                  {tLogin.whatShouldIDo}
                </p>
                <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                  <li>{tLogin.checkInboxForInvite}</li>
                  <li>{tLogin.checkSpamFolder}</li>
                  <li>{tLogin.useForgotPasswordHint}</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 mt-6">
          <Button
            variant="primary"
            fullWidth
            onClick={() => history.push('/login')}>
            {isRegistered ? tLogin.signIn : tLogin.goToLogin}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => history.push('/forgot-password')}>
            {tLogin.forgotPasswordButton}
          </Button>
          <button
            type="button"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mt-2"
            onClick={() => setShowAlreadyInvited(false)}>
            {tLogin.useDifferentEmail}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default AccountExists;
